import { useEffect, useRef } from "react";

/**
 * Subscribes to live auction events for a listing.
 * onEvent receives parsed JSON: { type: "bid"|"buy_now", amount, bidder_name, bid_count, auction_end, ... }
 */
export default function useAuctionSocket(listingId, onEvent) {
    const wsRef = useRef(null);
    const reconnectRef = useRef(null);
    const onEventRef = useRef(onEvent);

    useEffect(() => {
        onEventRef.current = onEvent;
    }, [onEvent]);

    useEffect(() => {
        if (!listingId) return undefined;

        const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
        const wsUrl = backendUrl.replace(/^http/, "ws") + `/api/ws/auctions/${listingId}`;

        let closed = false;
        let pingInterval = null;

        const connect = () => {
            if (closed) return;
            try {
                const ws = new WebSocket(wsUrl);
                wsRef.current = ws;

                ws.onmessage = (e) => {
                    if (e.data === "pong") return;
                    try {
                        const data = JSON.parse(e.data);
                        onEventRef.current?.(data);
                    } catch {
                        // ignore non-JSON frames
                    }
                };

                ws.onopen = () => {
                    pingInterval = setInterval(() => {
                        if (ws.readyState === WebSocket.OPEN) ws.send("ping");
                    }, 25000);
                };

                ws.onclose = () => {
                    if (pingInterval) clearInterval(pingInterval);
                    if (!closed) {
                        reconnectRef.current = setTimeout(connect, 2000);
                    }
                };

                ws.onerror = () => {
                    try { ws.close(); } catch { /* ignore */ }
                };
            } catch (e) {
                console.error("WS connect failed", e);
                reconnectRef.current = setTimeout(connect, 2000);
            }
        };

        connect();

        return () => {
            closed = true;
            if (pingInterval) clearInterval(pingInterval);
            if (reconnectRef.current) clearTimeout(reconnectRef.current);
            if (wsRef.current) {
                try { wsRef.current.close(); } catch { /* ignore */ }
            }
        };
    }, [listingId]);
}
